import React, { useEffect, useRef, useState } from 'react';
import { IoClose, IoSend, IoMic, IoMicOff, IoVideocam, IoVideocamOff, IoHandLeft, IoPersonRemove, IoCopyOutline, IoCheckmark } from 'react-icons/io5';
import { imageSrc } from '../../utils/avatar';
import { initialsOf, avatarColor } from '../../utils/meetingAvatar';
import { meetingLink } from '../../api/MeetingAPI';
import MeetingTranscript from './MeetingTranscript';
import { useLanguage } from '../../i18n/LanguageContext';

const avatarUrl = (imageUrl) => imageSrc(imageUrl);

function photoOf(metadata) {
    if (!metadata) return null;
    try {
        const parsed = JSON.parse(metadata);
        return parsed && parsed.imageUrl ? avatarUrl(parsed.imageUrl) : null;
    } catch (error) {
        return null;
    }
}

function Avatar({ identity, name, metadata }) {
    const photo = photoOf(metadata);
    if (photo) return <img className="MeetSideAvatar" src={photo} alt={name} />;
    return <span className="MeetSideAvatar MeetSideAvatarInitials" style={{ background: avatarColor(identity) }}>{initialsOf(name)}</span>;
}

function ChatTab({ messages, onSend, localIdentity }) {
    const { t, locale } = useLanguage();
    const [draft, setDraft] = useState('');
    const listRef = useRef(null);

    // Scrolls the list itself rather than calling scrollIntoView on a
    // sentinel. scrollIntoView walks every scrollable ancestor, and in a
    // layout built from overflow:hidden panels (which are still
    // programmatically scrollable) that can shunt the whole meeting out of
    // view with no scrollbar to bring it back.
    useEffect(() => {
        const list = listRef.current;
        if (list) list.scrollTop = list.scrollHeight;
    }, [messages.length]);

    const submit = (event) => {
        event.preventDefault();
        const text = draft.trim();
        if (!text) return;
        onSend(text);
        setDraft('');
    };

    return (
        <>
            <div className="MeetChatList" ref={listRef}>
                {messages.length === 0 && (
                    <p className="MeetSideEmpty">{t('meeting.chatEmpty')}</p>
                )}
                {messages.map((message) => (
                    <div key={message.id} className={`MeetChatMsg ${message.identity === localIdentity ? 'MeetChatMsgMine' : ''}`}>
                        <div className="MeetChatMeta">
                            <span className="MeetChatAuthor">{message.name}</span>
                            <span className="MeetChatTime">
                                {new Date(message.at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        </div>
                        <p className="MeetChatText">{message.text}</p>
                    </div>
                ))}
            </div>
            <form className="MeetChatForm" onSubmit={submit}>
                <input
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    placeholder={t('meeting.chatPlaceholder')}
                    maxLength={500}
                />
                <button type="submit" className="MeetChatSend primaryColor" disabled={!draft.trim()}><IoSend /></button>
            </form>
        </>
    );
}

function ParticipantsTab({ participants, raisedHands, isHost, onRemove, meeting }) {
    const { t } = useLanguage();
    const [copied, setCopied] = useState(false);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(meetingLink(meeting.code));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            // Nothing useful to do here - the same link is selectable from the
            // top bar's info popover, which is the documented fallback.
        }
    };

    // Hands first, then speakers, then everyone else - the two things worth
    // scrolling to are never below the fold.
    const ordered = [...participants].sort((a, b) => {
        const handDiff = Number(!!raisedHands[b.identity]) - Number(!!raisedHands[a.identity]);
        if (handDiff !== 0) return handDiff;
        return Number(b.speaking) - Number(a.speaking);
    });

    return (
        <>
            <button className="MeetInviteBtn secondaryColor" onClick={copyLink}>
                {copied ? <IoCheckmark /> : <IoCopyOutline />}
                {copied ? t('meeting.copied') : t('meeting.copyInvite')}
            </button>
            <div className="MeetPeopleList">
                {ordered.map((participant) => (
                    <div key={participant.identity} className="MeetPersonRow">
                        <Avatar identity={participant.identity} name={participant.name || participant.identity} metadata={participant.metadata} />
                        <span className="MeetPersonName">
                            {participant.name || participant.identity}
                            {participant.isLocal && <span className="MeetPersonYou"> {t('meeting.youSuffix')}</span>}
                        </span>
                        <span className="MeetPersonIcons">
                            {raisedHands[participant.identity] && <IoHandLeft className="MeetPersonHand" title={t('meeting.handRaised')} />}
                            {participant.cameraEnabled ? <IoVideocam /> : <IoVideocamOff className="MeetPersonOff" />}
                            {participant.muted ? <IoMicOff className="MeetPersonOff" /> : <IoMic />}
                            {isHost && !participant.isLocal && (
                                <button
                                    className="MeetPersonRemove"
                                    title={t('meeting.removeParticipant')}
                                    onClick={() => onRemove(participant.identity, participant.name || participant.identity)}
                                >
                                    <IoPersonRemove />
                                </button>
                            )}
                        </span>
                    </div>
                ))}
            </div>
        </>
    );
}

// One panel with two tabs rather than two panels: on a laptop there is only
// room for one column beside the stage, and chat and the roster are never
// needed at the same instant.
export default function MeetingSidePanel({
    tab, onTabChange, onClose,
    messages, onSendMessage, localIdentity,
    participants, raisedHands, isHost, onRemove, meeting,
    transcript, onToggleTranscript, transcriptBusy
}) {
    const { t } = useLanguage();

    return (
        <aside className="MeetSidePanel glass">
            <div className="MeetSideHeader">
                <div className="MeetSideTabs">
                    <button
                        className={`MeetSideTab ${tab === 'chat' ? 'MeetSideTabActive' : ''}`}
                        onClick={() => onTabChange('chat')}
                    >
                        {t('meeting.chat')}
                    </button>
                    <button
                        className={`MeetSideTab ${tab === 'people' ? 'MeetSideTabActive' : ''}`}
                        onClick={() => onTabChange('people')}
                    >
                        {t('meeting.participants')} ({participants.length})
                    </button>
                    <button
                        className={`MeetSideTab ${tab === 'transcript' ? 'MeetSideTabActive' : ''}`}
                        onClick={() => onTabChange('transcript')}
                    >
                        {t('meeting.transcript.tab')}
                        {/* A dot rather than a count: the point is only that
                            something is being recorded right now. */}
                        {(transcript.status === 'active' || transcript.status === 'connecting') && <span className="MeetSideTabDot" />}
                    </button>
                </div>
                <button className="MeetSideClose" onClick={onClose} title={t('meeting.close')}><IoClose /></button>
            </div>

            {tab === 'chat' && <ChatTab messages={messages} onSend={onSendMessage} localIdentity={localIdentity} />}
            {tab === 'people' && <ParticipantsTab participants={participants} raisedHands={raisedHands} isHost={isHost} onRemove={onRemove} meeting={meeting} />}
            {tab === 'transcript' && <MeetingTranscript transcript={transcript} isHost={isHost} onToggle={onToggleTranscript} busy={transcriptBusy} />}
        </aside>
    );
}
